import type React from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { ReplaySubject, asyncScheduler, combineLatest, merge } from 'rxjs';
import { map, scan, shareReplay, throttleTime } from 'rxjs/operators';
import { v1 as uuid } from 'uuid';

import {
  DecisionStatusEnum,
  ExecTypeEnum,
  Get,
  NEW_ORDER_SINGLE,
  ORDER,
  ORDER_CANCEL_REQUEST,
  ORDER_CONTROL_REQUEST,
  ORDER_MODIFY_REQUEST,
  OrderControlActionEnum,
  POST,
  PUT,
  PricingMode,
  formattedDateForSubscription,
  frontendAllocationsToBackendAllocations,
  request,
  useCombinedErrorObs,
  useConstant,
  useObservable,
  useSecuritiesContext,
  useSocketClient,
  useStaticSubscription,
  useUserContext,
  useWsPaginationLimiter,
  wsStitchWith,
  wsWaitForAllPages,
  type ExecutionReport,
  type Order,
  type RequestStream,
} from '@talos/kyoko';

import { assertOrderCurrency, cleanParametersForOrder } from '@src/utils/order';

import { shouldSendDurationOnModify } from '../components/OMS/NewOrder/utils';
import { ORDER_SUB_THROTTLE } from '../containers/Blotters/Orders/tokens';
import { useOpenCustomerOrders } from '../containers/Blotters/Orders/useOpenCustomerOrders';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuxiliaryOrderData } from './AuxiliaryOrderDataProvider';
import { OPEN_ORDER_STATUSES } from './constants';
import type {
  FullOrderRequest,
  ModifyOrderArgs,
  ModifyOrderRequest,
  OrdersContextProps,
  SendOrderArgs,
} from './orders.types';
import { ordersProviderMergeOrderUpdate } from './ordersProviderMergeOrderUpdate';
import { OrgConfigurationKey, useOrgConfiguration } from './OrgConfigurationProvider';

const RECENT_ORDERS_THROTTLE_MS = 1_000;
const RECENT_ORDERS_RECORDS_MAX = 10_000;

const Orders = createContext<OrdersContextProps | undefined>(undefined);
Orders.displayName = 'OrdersContext';

export const useOrders = () => {
  const context = useContext(Orders);
  if (context === undefined) {
    throw new Error('Missing OrderContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const OrdersProvider = memo(function OrdersProvider(props: React.PropsWithChildren<unknown>) {
  const client = useSocketClient();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { orgApiEndpoint } = useUserContext();
  const { enableOrderAuxData, enableDurationSubmission } = useFeatureFlag();

  const send = useCallback(
    (
      {
        symbol,
        side,
        orderQty,
        group,
        price,
        pricingMode,
        parameters,
        marketAccounts,
        subAccountAllocations,
        clOrdID,
        ordType,
        timeInForce,
        selectedStrategy,
        subAccounts,
        allocationValueType,
        useTradeAllocations,
        subAccount,
        orderCurrency,
        expectedFillPrice,
        expectedFillQty,
        comment,
        legParams,
        initialDecisionStatus,
        ...rest
      }: SendOrderArgs,
      _transactTime = new Date()
    ): boolean => {
      const _typecheck: EmptyObject = rest;
      const modifiedParameters = cleanParametersForOrder({
        parameters,
        selectedStrategy,
        enableDurationSubmission: enableDurationSubmission || initialDecisionStatus === DecisionStatusEnum.Staged,
      });
      const data: FullOrderRequest = {
        Symbol: symbol,
        ClOrdID: clOrdID,
        Side: side,
        TransactTime: formattedDateForSubscription(_transactTime),
        OrderQty: orderQty,
        OrdType: ordType as string, // This is weird. Should the request type have the OrdType enum? when does the enum become a string?
        Price: price,
        TimeInForce: timeInForce,
        Markets: marketAccounts,
        Parameters: modifiedParameters,
        ExpectedFillPrice: expectedFillPrice,
        ExpectedFillQty: expectedFillQty,
        Comments: comment,
        LegParams: legParams,
      };
      if (group) {
        data.Group = group;
      }
      if (selectedStrategy) {
        data.Strategy = selectedStrategy.Name;
      }
      if (orderCurrency) {
        data.Currency = orderCurrency;
      }
      if (initialDecisionStatus) {
        data.InitialDecisionStatus = initialDecisionStatus;
      }
      // Only include PricingMode when it differs from Default
      if (pricingMode && pricingMode !== PricingMode.Default) {
        data.PricingMode = pricingMode;
      }
      if (subAccounts && subAccounts?.length > 0) {
        if (useTradeAllocations) {
          const preparedTradeAllocations = frontendAllocationsToBackendAllocations({
            subAccountAllocations,
            subAccounts,
            allocationValueType,
            quantity: orderQty,
          });
          data.Allocation = preparedTradeAllocations;
        } else if (subAccountAllocations?.length === 1) {
          data.SubAccount = subAccountAllocations[0].subAccount;
        } else if (subAccount) {
          // Click to Trade
          data.SubAccount = subAccount;
        }
      }

      if (!assertOrderCurrency(data, securitiesBySymbol.get(data.Symbol)!)) {
        return false;
      }

      client.registerPublication({
        type: NEW_ORDER_SINGLE,
        data: [data],
      });
      return true;
    },
    [client, securitiesBySymbol, enableDurationSubmission]
  );

  const modify = useCallback(
    (
      {
        symbol,
        price,
        orderID,
        clOrdID,
        orderQty,
        marketAccounts,
        subAccountAllocations,
        parameters,
        group,
        selectedStrategy,
        subAccounts,
        allocationValueType,
        useTradeAllocations,
        subAccount,
        comment,
        legParams,
        decisionStatus,
      }: ModifyOrderArgs,
      _transactTime = new Date()
    ) => {
      const modifiedParameters = cleanParametersForOrder({
        parameters,
        selectedStrategy,
        enableDurationSubmission: shouldSendDurationOnModify({
          decisionStatus,
          enableDurationSubmission,
        }),
      });
      const data: ModifyOrderRequest = {
        Symbol: symbol,
        Price: price,
        OrderID: orderID,
        ClOrdID: clOrdID,
        TransactTime: formattedDateForSubscription(_transactTime) || '',
        OrderQty: orderQty,
        Markets: marketAccounts,
        Strategy: selectedStrategy.Name,
        Parameters: modifiedParameters,
        Group: group,
        Comments: comment,
      };
      if (legParams) {
        data.LegParams = legParams;
      }
      if (subAccounts && subAccounts?.length > 0) {
        if (useTradeAllocations) {
          const preparedTradeAllocations = frontendAllocationsToBackendAllocations({
            subAccountAllocations,
            subAccounts,
            allocationValueType,
            quantity: orderQty,
          });
          data.Allocation = preparedTradeAllocations;
        } else if (subAccountAllocations?.length === 1) {
          data.SubAccount = subAccountAllocations[0].subAccount;
        } else if (subAccount) {
          // Click to Trade
          data.SubAccount = subAccount;
        }
      }
      // Safeguard against poorly formatted or not required SubAccount selection
      if (data.SubAccount == null || data.SubAccount === '') {
        delete data.SubAccount;
      }

      client.registerPublication({
        type: ORDER_MODIFY_REQUEST,
        data: [data],
      });
    },
    [client, enableDurationSubmission]
  );
  // More then Group is returned by this RestEndpoint but it is the only property caller of this crappy function need
  const historicalOrders = useCallback((endpoint: string) => {
    const queryString = new URLSearchParams();
    queryString.append('offset', '0');
    queryString.append('limit', '100');
    queryString.append('orderBy', '-SubmitTime');
    return Get<{ data: Pick<ExecutionReport, 'Group'>[] }>(
      endpoint,
      `/organization/blotters/OMSOrders?${queryString.toString()}`
    );
  }, []) satisfies OrdersContextProps['historicalOrders'];

  const cancel = useCallback(
    (orderID?: string) =>
      client.registerPublication({
        type: ORDER_CANCEL_REQUEST,
        data: [
          {
            ClOrdID: uuid(),
            OrderID: orderID,
            TransactTime: formattedDateForSubscription(new Date()),
          },
        ],
      }),
    [client]
  );

  const pause = useCallback(
    (orderID?: string) =>
      client.registerPublication({
        type: ORDER_CONTROL_REQUEST,
        data: [
          {
            ClOrdID: uuid(),
            OrderID: orderID,
            Action: OrderControlActionEnum.Pause,
          },
        ],
      }),
    [client]
  );

  const resume = useCallback(
    (orderID?: string) =>
      client.registerPublication({
        type: ORDER_CONTROL_REQUEST,
        data: [
          {
            ClOrdID: uuid(),
            OrderID: orderID,
            Action: OrderControlActionEnum.Resume,
          },
        ],
      }),
    [client]
  );

  // Recent Orders (Last 2 days)
  // TODO why are we doing this? why can't we just subscribe to open orders only? might be legacy from when this was used for the
  // recent orders blotter but now it's only being used for the open orders blotter.
  // TODO should this include the loadAll flag? I think if we don't load all of the orders
  const recentReq = useMemo(() => {
    const r: RequestStream = {
      name: ORDER,
      tag: 'RECENT_ORDERS',
      StartDate: formattedDateForSubscription(Sugar.Date.create('2 days ago')),
      sort_by: '-SubmitTime',
      HideApiCalls: true,
      Throttle: ORDER_SUB_THROTTLE,
      CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
    };
    if (enableOrderAuxData) {
      r.IncludeAuxiliaryData = true;
    }
    return r;
  }, [enableOrderAuxData]);
  const {
    data: recentSub,
    error: recentOrdersSubError,
    nextPage: recentSubNextPage,
  } = useStaticSubscription<Order>(
    recentReq,
    { loadAll: false } // set to false and control from the pagination limiter below
  );

  // Open Orders (All time)
  const openReq = useMemo(() => {
    const r: RequestStream = {
      name: ORDER,
      tag: 'OPEN_ORDERS',
      Statuses: OPEN_ORDER_STATUSES,
      sort_by: '-Timestamp',
      HideApiCalls: true,
      Throttle: ORDER_SUB_THROTTLE,
      CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
    };
    if (enableOrderAuxData) {
      r.IncludeAuxiliaryData = true;
    }
    return r;
  }, [enableOrderAuxData]);
  const { data: openSub, error: openOrdersSubError } = useStaticSubscription<Order>(
    openReq,
    { loadAll: true } // loadAll defaults to true. We're just being explicit here for clarity.
  );

  const recentOrdersError = useCombinedErrorObs([recentOrdersSubError, openOrdersSubError]);

  const { getConfig } = useOrgConfiguration();
  const limit = useConstant(getConfig(OrgConfigurationKey.RecentOrderRowsMax, RECENT_ORDERS_RECORDS_MAX));
  const recentSubPaginationLimiter = useWsPaginationLimiter<Order>({
    startingLimit: limit,
    nextPage: recentSubNextPage,
  });
  const pageLimitedRecentSub = useMemo(
    () => recentSub.pipe(recentSubPaginationLimiter.wsPaginationLimiterInstance),
    [recentSub, recentSubPaginationLimiter.wsPaginationLimiterInstance]
  );

  const openCustomerOrdersObs = useOpenCustomerOrders();

  // Hedge order status update stitching onto orders
  const { hedgeOrderStatusObs, orderAnnotationsObs } = useAuxiliaryOrderData();

  const enrichedOpenSub = useMemo(() => {
    return openSub.pipe(
      wsWaitForAllPages({ getUniqueKey: order => order.OrderID }),
      wsStitchWith({
        secondarySource: hedgeOrderStatusObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: hos => hos.InitiatingOrderID,
        stitch: (order, hos) => {
          if (hos) {
            order.enrichWithHedgeOrderStatus(hos);
          }
          return order;
        },
      }),
      wsStitchWith({
        secondarySource: orderAnnotationsObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: annotation => annotation.OrderID,
        stitch: (order, annotation) => {
          if (annotation) {
            order.enrichWithOrderAnnotation(annotation);
          }
          return order;
        },
      })
    );
  }, [hedgeOrderStatusObs, openSub, orderAnnotationsObs]);

  const enrichedRecentSub = useMemo(() => {
    return pageLimitedRecentSub.pipe(
      wsStitchWith({
        secondarySource: hedgeOrderStatusObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: hos => hos.InitiatingOrderID,
        stitch: (order, hos) => {
          if (hos) {
            order.enrichWithHedgeOrderStatus(hos);
          }
          return order;
        },
      }),
      wsStitchWith({
        secondarySource: orderAnnotationsObs,
        getPrimaryTypeKey: order => order.OrderID,
        getSecondaryTypeKey: annotation => annotation.OrderID,
        stitch: (order, annotation) => {
          if (annotation) {
            order.enrichWithOrderAnnotation(annotation);
          }
          return order;
        },
      })
    );
  }, [hedgeOrderStatusObs, orderAnnotationsObs, pageLimitedRecentSub]);

  // Make sure we're always storing the data received from the two subscriptions above,
  // even if no one is listening on e.g. the `recentOrders` observable.
  const recentAndOpenOrdersObs = useConstant(new ReplaySubject<Map<string, Order>>(1));
  useEffect(() => {
    const subscription = merge(enrichedRecentSub, enrichedOpenSub)
      .pipe(scan(ordersProviderMergeOrderUpdate, new Map<string, Order>()))
      .subscribe(orders => recentAndOpenOrdersObs.next(orders));
    return () => {
      subscription.unsubscribe();
    };
  }, [enrichedOpenSub, enrichedRecentSub, recentAndOpenOrdersObs]);

  // Recent Orders (Combined)
  const recentOrders = useObservable(
    () =>
      combineLatest([recentAndOpenOrdersObs, openCustomerOrdersObs]).pipe(
        map(([orders, openCustomerOrders]) =>
          Array.from(orders.values()).map((order: Order) => {
            if (order.ParentOrderID && openCustomerOrders.has(order.ParentOrderID)) {
              const customerOrder = openCustomerOrders.get(order.ParentOrderID);
              order.enrichOrderWithCustomerOrder(customerOrder);
            }
            return order;
          })
        ),
        throttleTime(RECENT_ORDERS_THROTTLE_MS, asyncScheduler, {
          leading: true,
          trailing: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [recentAndOpenOrdersObs, openCustomerOrdersObs]
  );

  const archive = useCallback(
    (orderID: string) => request(POST, `${orgApiEndpoint}/orders/${orderID}/archive`),
    [orgApiEndpoint]
  );

  const unarchive = useCallback(
    (orderID: string) => request(POST, `${orgApiEndpoint}/orders/${orderID}/unarchive`),
    [orgApiEndpoint]
  );

  const forceCancel = useCallback(
    (orderID: string) => request(PUT, `${orgApiEndpoint}/orders/${orderID}/force-cancel`),
    [orgApiEndpoint]
  );

  return (
    <Orders.Provider
      value={{
        recentOrders,
        recentOrdersError,
        historicalOrders,
        send,
        modify,
        cancel,
        pause,
        resume,
        archive,
        unarchive,
        forceCancel,
      }}
    >
      {props.children}
    </Orders.Provider>
  );
});
